import { MainMenuItem, SubMenuItem } from './Drawer.props';

/** A menu node that can itself hold children — a top-level section or a nested group. */
type Container = MainMenuItem | SubMenuItem;

/**
 * Does `pathname` fall under `url`, at a path-segment boundary? True for an
 * exact match, or when `pathname` continues past `url` with another `/` —
 * never on a bare string prefix, so a link to `/services` doesn't light up
 * for `/services-import`. `url: '/'` (Home) only ever matches `pathname`
 * `/` itself: `'/' + '/'` never appears as a real boundary, so Home never
 * accidentally wins as an ancestor of every route.
 */
const isBoundaryMatch = (pathname: string, url: string): boolean =>
  pathname === url || pathname.startsWith(`${url}/`);

/** Every url in the menu tree that could possibly be matched against. */
const collectUrls = (menuData: MainMenuItem[]): string[] => {
  const urls: string[] = [];
  const visitSub = (sub: SubMenuItem) => {
    if (sub.url) urls.push(sub.url);
    (sub.subMenuItems ?? []).forEach((child) => {
      if (child.url) urls.push(child.url);
    });
  };
  menuData.forEach((item) => {
    if (item.url) urls.push(item.url);
    (item.subMenuItems ?? []).forEach(visitSub);
  });
  return urls;
};

/**
 * Every url nested under `container` — its own, plus its children's (a
 * container's children are never containers themselves beyond one more
 * level, since `ChildMenuItem` never carries a further `subMenuItems`).
 */
const urlsUnder = (container: Container): string[] => {
  const urls: string[] = [];
  if (container.url) urls.push(container.url);
  (container.subMenuItems ?? []).forEach((sub) => {
    if (sub.url) urls.push(sub.url);
    ((sub as SubMenuItem).subMenuItems ?? []).forEach((child) => {
      if (child.url) urls.push(child.url);
    });
  });
  return urls;
};

/**
 * The longest leading path shared by every url nested under `container` — its
 * "namespace". E.g. Parts' children are `/parts/search`, `/parts/createRepair`,
 * `/parts/movements`... → namespace `/parts`, even though no menu item carries
 * that bare url itself. `null` when the container has no urls under it at all.
 */
const namespaceOf = (container: Container): string | null => {
  const urls = urlsUnder(container).filter(Boolean);
  if (!urls.length) return null;
  const segmentLists = urls.map((url) => url.split('/').filter(Boolean));
  const shortestLength = Math.min(...segmentLists.map((segments) => segments.length));
  const shared: string[] = [];
  for (let i = 0; i < shortestLength; i++) {
    const segment = segmentLists[0][i];
    if (!segmentLists.every((segments) => segments[i] === segment)) break;
    shared.push(segment);
  }
  return shared.length ? `/${shared.join('/')}` : null;
};

/** Every section and nested group in the tree — the candidates a route can fall back to. */
const collectContainers = (menuData: MainMenuItem[]): Container[] => {
  const containers: Container[] = [];
  const visit = (container: Container) => {
    containers.push(container);
    (container.subMenuItems ?? []).forEach((sub) => {
      // A container's children are `SubMenuItem`s (which can themselves be
      // groups) at every level except one below a group, where they're
      // `ChildMenuItem`s — those never carry `subMenuItems` at all.
      if ((sub as SubMenuItem).subMenuItems?.length) visit(sub as SubMenuItem);
    });
  };
  menuData.forEach((item) => {
    if (item.subMenuItems?.length) visit(item);
  });
  return containers;
};

/** This container's own flat (non-group) child literally named "Search", if it has one. */
const searchUrlOf = (container: Container): string | null =>
  (container.subMenuItems ?? []).find((sub) => sub.name === 'Search' && sub.url)?.url ?? null;

/**
 * Resolves the pathname to the single menu url that should be treated as
 * "current". Two tiers:
 *
 * 1. The longest url that boundary-matches — so a detail route not itself in
 *    the menu (e.g. `/services/:id`, reached from a job search result or a
 *    link on another page rather than the nav itself) resolves to its owning
 *    list page (`/services`), and a route nested under a more specific
 *    sibling (e.g. `/services/import/step2`) resolves to that sibling
 *    (`/services/import`) rather than the shorter, less specific `/services`.
 * 2. If nothing matches directly, the most specific section or group whose
 *    *namespace* contains the route falls back to its own "Search" item —
 *    the default page for that area. This covers groups that don't happen to
 *    register a bare url of their own for their list page (Parts' Search is
 *    `/parts/search`, not `/parts`), so a detail route sitting directly under
 *    the shared segment (`/parts/:id`) would otherwise match nothing at all.
 *
 * Returns `null` only when neither tier finds anything — the route isn't
 * under any menu section's namespace at all.
 */
export const findActiveUrl = (menuData: MainMenuItem[], pathname: string): string | null => {
  const direct = collectUrls(menuData).reduce<string | null>(
    (best, url) =>
      isBoundaryMatch(pathname, url) && (!best || url.length > best.length) ? url : best,
    null
  );
  if (direct) return direct;

  const containers = collectContainers(menuData)
    .map((container) => ({ container, namespace: namespaceOf(container) }))
    .filter((c): c is { container: Container; namespace: string } => !!c.namespace)
    .sort((a, b) => b.namespace.length - a.namespace.length);

  const owning = containers.find((c) => isBoundaryMatch(pathname, c.namespace));
  return owning ? searchUrlOf(owning.container) : null;
};

/**
 * Is this menu item's own url the resolved active url? `url` is optional/
 * blank for pure containers (e.g. "Quick Views" has no url of its own),
 * which this never matches.
 */
export const isActive = (activeUrl: string | null, url?: string): boolean =>
  !!url && !!activeUrl && url === activeUrl;

/**
 * Does this sub-item contain the active url — either as its own link, or
 * as one of its nested children (for sub-items that are themselves a group,
 * e.g. "Quick Views")?
 */
export const subItemContainsActive = (activeUrl: string | null, sub: SubMenuItem): boolean => {
  if (isActive(activeUrl, sub.url)) return true;
  return (sub.subMenuItems ?? []).some((child) => isActive(activeUrl, child.url));
};

/**
 * Does this top-level section contain the active url anywhere within it —
 * its own url, a direct sub-item, or a nested group's child? Used to keep a
 * section's header showing "you're in here" even while it's collapsed.
 */
export const sectionContainsActive = (activeUrl: string | null, item: MainMenuItem): boolean => {
  if (isActive(activeUrl, item.url)) return true;
  return (item.subMenuItems ?? []).some((sub) => subItemContainsActive(activeUrl, sub));
};
